import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { LockScreen } from '../components/LockScreen';
import { StagingUserSwitcher } from '../components/StagingUserSwitcher';
import { configureAutoUpdate } from '../hooks/useAutoUpdate';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { useAuth } from '../lib/auth/hooks';
import { useLockState } from '../lib/auth/lock';
import { verifyOfflineCredential } from '../lib/auth/offline-credentials';
import {
  type SecurityPolicy,
  DEFAULT_POLICY,
  fetchSecurityPolicy,
  getStoredSecurityPolicy,
  storeSecurityPolicy,
} from '../lib/auth/security-policy';
import { FeatureFlagProvider } from '../lib/feature-flags/context';
import { graphqlClient, setAccessToken } from '../lib/graphql/client';
import { ORG_SETTINGS_QUERY, USER_PREFERENCES_QUERY } from '../lib/graphql/queries/core';

/** Safe defaults when GraphQL queries fail (network error, PostGraphile down) */
const AUTO_UPDATE_DEFAULTS = {
  updateChannel: 'stable' as const,
  minVersion: null,
  autoUpdateEnabled: true,
  betaTester: false,
};

export function AuthLayout() {
  const { session, loading, hasZooConfig, zooConfig, signOut } = useAuth();
  const configuredRef = useRef(false);
  const [securityPolicy, setSecurityPolicy] = useState<SecurityPolicy>(getStoredSecurityPolicy);

  const handleSessionExpired = useCallback(() => {
    console.warn('[AuthLayout] Session expired by policy — signing out');
    signOut();
  }, [signOut]);

  useSessionTimeout(
    securityPolicy,
    session?.user?.created_at ?? null,
    handleSessionExpired,
  );

  const { isLocked, unlock } = useLockState(securityPolicy, !!session);

  // Tier 2: configure auto-updater after login with org_settings + user_preferences
  useEffect(() => {
    if (!session || configuredRef.current) return;
    configuredRef.current = true;

    const userId = session.user.id;

    // Ensure GraphQL client has the access token before querying
    // (setAccessToken from AuthProvider may not have fired yet in this render cycle)
    setAccessToken(session.access_token);

    // Fetch org_settings and user_preferences in parallel via PostGraphile
    Promise.all([
      graphqlClient.query(ORG_SETTINGS_QUERY, {}, { requestPolicy: 'network-only' }).toPromise(),
      graphqlClient
        .query(USER_PREFERENCES_QUERY, { userId }, { requestPolicy: 'network-only' })
        .toPromise(),
    ])
      .then(([orgResult, prefsResult]) => {
        // Log errors from GraphQL (query-level errors, not network errors)
        if (orgResult.error) {
          console.error(
            '[AuthLayout] org_settings query error:',
            orgResult.error.message,
            orgResult.error,
          );
        }
        if (prefsResult.error) {
          console.error(
            '[AuthLayout] user_preferences query error:',
            prefsResult.error.message,
            prefsResult.error,
          );
        }

        const orgSettings = orgResult.data?.allOrgSettings?.nodes?.[0];
        const userPrefs = prefsResult.data?.allUserPreferences?.nodes?.[0];

        configureAutoUpdate({
          updateChannel: orgSettings?.updateChannel ?? AUTO_UPDATE_DEFAULTS.updateChannel,
          minVersion: orgSettings?.minVersion ?? AUTO_UPDATE_DEFAULTS.minVersion,
          autoUpdateEnabled:
            orgSettings?.autoUpdateEnabled ?? AUTO_UPDATE_DEFAULTS.autoUpdateEnabled,
          betaTester: userPrefs?.betaTester ?? AUTO_UPDATE_DEFAULTS.betaTester,
        });
      })
      .catch((err: unknown) => {
        // Graceful degradation: use safe defaults if GraphQL is unreachable
        console.error(
          '[AuthLayout] Failed to fetch auto-update settings (network/exception):',
          err,
        );
        configureAutoUpdate(AUTO_UPDATE_DEFAULTS);
      });

    // Fetch platform security policy (non-blocking — cached defaults used until loaded)
    if (zooConfig?.orgId && zooConfig.orgId !== 'staging') {
      fetchSecurityPolicy(zooConfig.orgId)
        .then((policy) => {
          storeSecurityPolicy(policy);
          setSecurityPolicy(policy);
        })
        .catch((err: unknown) => {
          console.warn('[AuthLayout] Failed to fetch security policy, using cached/defaults:', err);
        });
    }
  }, [session, zooConfig]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          fontFamily: 'var(--font-family-primary)',
          color: 'var(--color-label-tertiary)',
        }}
      >
        Loading...
      </div>
    );
  }

  // No zoo config or no session → unified auth page
  if (!hasZooConfig || !session) {
    return <Navigate to="/auth" replace />;
  }

  if (isLocked) {
    return (
      <LockScreen
        userEmail={session.user.email ?? ''}
        userName={session.user.user_metadata?.full_name ?? session.user.email ?? ''}
        onUnlock={unlock}
        onVerifyOffline={async (email, password) => {
          const userId = await verifyOfflineCredential(email, password);
          return userId !== null;
        }}
      />
    );
  }

  return (
    <FeatureFlagProvider>
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar will be added in Sprint 2 */}
        <main style={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </main>
        {/* Temporary logout button — will move to sidebar/user menu in Sprint 2 */}
        <button
          type="button"
          onClick={() => signOut()}
          style={{
            position: 'fixed',
            bottom: 16,
            right: 76,
            padding: '8px 16px',
            borderRadius: 8,
            border: '1px solid var(--color-border-opaque, #ddd)',
            background: 'var(--color-bg-secondary, #f5f5f5)',
            color: 'var(--color-label-secondary, #666)',
            fontSize: 13,
            cursor: 'pointer',
            zIndex: 1000,
          }}
        >
          Odhlásit se
        </button>
        <StagingUserSwitcher />
      </div>
    </FeatureFlagProvider>
  );
}
